import { colors } from "@/constant/colors";
import { useEffect, useState } from "react";
import ExpandExplanation from "./expandExplanation";
import { HighlighterQuestion, HighlighterSelector, isHighlightAnswerCorrect, getUniqueTags } from "./highlighter-question";
import NormalButton from "../Button/normal";


interface PracticeHighlightProps {
  question: any;
  handleAnswer: (option: string, isCorrect: boolean, autoSubmit?: boolean) => void;
  currentAnswer: any;
  allFinished: boolean;
  disabled: boolean;
  isReview?: boolean;
  onInputChange?: (value: string) => void;
  triggerCheck?: boolean;
}

const PracticeHighlight = ({ question, handleAnswer, currentAnswer, allFinished, disabled, isReview = false, onInputChange, triggerCheck }: PracticeHighlightProps) => {

  const [selectedTag, setSelectedTag] = useState<string>("");

  const [submit, setSubmit] = useState(false);

  const [highlighted, setHighlighted] = useState<{ index: number; tag: string }[]>([]);

  useEffect(() => {
    if (currentAnswer.isCorrect === null) {
      reset();
    }
  }, [currentAnswer]);

  const getStandardAnswer = (answer: any) => {
    // 如果 answer 是字符串，尝试解析成 highlight 数组
    if (typeof answer === 'string') {
      try {
        const parsed = JSON.parse(answer);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        // 如果解析失败，返回空数组
        return [];
      }
    }
    // 如果 answer 已经是数组，直接返回
    if (Array.isArray(answer)) {
      return answer;
    }
    // 其他情况返回空数组
    return [];
  }

  const reset = () => {
    const standardAnswer = getStandardAnswer(question.answer);
    setHighlighted([]); // 用户应该从空白开始选择，而不是预填答案
    setSubmit(false);
    setSelectedTag(getUniqueTags(standardAnswer)[0]);
  }

  // 提交按钮逻辑
  const handleSubmit = () => {
    if (disabled || submit) return;
    setSubmit(true);
    const standardAnswer = getStandardAnswer(question.answer);
    const isCorrect = isHighlightAnswerCorrect(question.question, highlighted, standardAnswer);
    handleAnswer(JSON.stringify(highlighted), isCorrect, true); // autoSubmit = true
  };

  // 响应外部的Check触发
  useEffect(() => {
    if (triggerCheck && !submit) {
      handleSubmit();
    }
  }, [triggerCheck]);

  // 高亮变化时通知父组件
  const handleHighlightChange = (newHighlighted: { index: number; tag: string }[]) => {
    setHighlighted(newHighlighted);
    if (onInputChange) {
      onInputChange(JSON.stringify(newHighlighted));
    }
  };

  const isSubmitDisabled = disabled || submit;

  return (
    <div className="flex flex-col w-full h-full items-center">
      <div className="max-w-[800px] flex flex-col w-fit h-full items-start gap-8">
        {!isReview && <div className="flex flex-row items-center text-base" style={{ color: colors.mainTextColor }}>Click the words in the sentence to highlight them with the corresponding colors.</div>}
        <div className="flex flex-row items-center justify-center w-full">
          <HighlighterSelector answer={getStandardAnswer(question.answer)} selectedTag={selectedTag} onSelect={setSelectedTag} isReview={isReview} />
        </div>
        <HighlighterQuestion className="emphasize-text" question={question.question} correctAnswer={getStandardAnswer(question.answer)} userAnswer={highlighted} disabled={isSubmitDisabled} selectedTag={selectedTag} onHighlightChange={handleHighlightChange} mode={isReview ? 'truth' : submit ? 'judge' : 'user'} />
      </div>
      {
        currentAnswer.selectedOption !== null && !currentAnswer.isCorrect && (
          <div className="max-w-[800px] flex flex-col w-fit h-full items-start gap-8">
            <span className="text-xl emphasize-text" style={{ color: colors.mainTextColor }}>The correct answer is:</span>
            <HighlighterQuestion className="emphasize-text" question={question.question} correctAnswer={getStandardAnswer(question.answer)} userAnswer={[]} disabled={true} selectedTag={""} mode={'truth'} onHighlightChange={() => { }} />
          </div>
        )
      }
      {(submit || isReview) && (
        <ExpandExplanation explanation={question?.explanation || ''} initialHideExplanation={currentAnswer.isCorrect} />
      )}
    </div>
  );
};

export default PracticeHighlight;